import { proxyCall, DbResult } from './base';

export interface CmsBlock {
  id: string;
  pageKey: string;
  blockKey: string;
  lang: string;
  contentJson: string;
  updatedAt: string;
}

export interface HomePageBlock {
  id: string;
  blockKey: string;
  contentJson: string;
  updatedAt: string;
}

export interface FactoryPageBlock {
  id: string;
  blockKey: string;
  lang: string;
  subtitle: string;
  title: string;
  description: string;
  image: string;
  cam: string;
  bgNumber: string;
  updatedAt: string;
}

export interface FactorySectionBlock {
  id: string;
  sectionKey: string;
  lang: string;
  title: string;
  subtitle: string;
  description: string;
  updatedAt: string;
}

export interface EsgContentBlock {
  id: string;
  blockKey: string;
  lang: string;
  contentJson: string;
  updatedAt: string;
}

export const cmsBlocksDb = {
  list: () =>
    proxyCall<CmsBlock[]>('list', 'cmsBlocks', { orderBy: { updatedAt: 'desc' } }),
  getByPage: (pageKey: string) =>
    proxyCall<CmsBlock[]>('list', 'cmsBlocks', { where: { pageKey } }),
  upsert: (data: Partial<CmsBlock> & { id: string }) =>
    proxyCall('upsert', 'cmsBlocks', { ...data, updatedAt: new Date().toISOString() }),
  delete: (id: string) =>
    proxyCall('delete', 'cmsBlocks', { id }),
};

export const siteContentDb = {
  list: (section: string) =>
    proxyCall<any[]>('list', 'site_content', { where: { section } }),
  upsert: (data: any) =>
    proxyCall('upsert', 'site_content', data),
  delete: (id: string) =>
    proxyCall('delete', 'site_content', { id }),
};

export const homePageContentDb = {
  async get(blockKey: string): Promise<DbResult<HomePageBlock | null>> {
    return proxyCall<HomePageBlock | null>('get', 'homePageContent', { id: blockKey });
  },
  async upsert(blockKey: string, content: Record<string, unknown>): Promise<DbResult<HomePageBlock>> {
    return proxyCall<HomePageBlock>('upsert', 'homePageContent', {
      id: blockKey,
      blockKey,
      contentJson: JSON.stringify(content),
      updatedAt: new Date().toISOString(),
    });
  },
  async listAll(): Promise<DbResult<HomePageBlock[]>> {
    return proxyCall<HomePageBlock[]>('list', 'homePageContent', { limit: 100 });
  },
};

export const factoryPageContentDb = {
  list: (lang = 'ru') =>
    proxyCall<FactoryPageBlock[]>('list', 'factoryPageContent', { where: { lang } }),
  upsert: (data: Partial<FactoryPageBlock> & { id: string }) =>
    proxyCall('upsert', 'factoryPageContent', { ...data, updatedAt: new Date().toISOString() }),
};

export const factorySectionContentDb = {
  listByKey: (sectionKey: string) =>
    proxyCall<FactorySectionBlock[]>('list', 'factorySectionContent', { where: { sectionKey } }),
  upsert: (data: Partial<FactorySectionBlock> & { id: string }) =>
    proxyCall('upsert', 'factorySectionContent', { ...data, updatedAt: new Date().toISOString() }),
};

export const esgContentDb = {
  listByLang: (lang: string) =>
    proxyCall<EsgContentBlock[]>('list', 'esgContent', { where: { lang } }),
  get: (blockKey: string, lang: string) =>
    proxyCall<EsgContentBlock[]>('list', 'esgContent', { where: { blockKey, lang } }),
  upsert: (blockKey: string, lang: string, content: Record<string, unknown>) =>
    proxyCall('upsert', 'esgContent', {
      id: `esg-${blockKey}-${lang}`,
      blockKey,
      lang,
      contentJson: JSON.stringify(content),
      updatedAt: new Date().toISOString(),
    }),
  delete: (id: string) => proxyCall('delete', 'esgContent', { id }),
};

export const becomeDealerContentDb = {
  listByLang: (lang: string) =>
    proxyCall<CmsBlock[]>('list', 'cmsBlocks', {
      where: { pageKey: 'become-dealer', lang },
    }),
  upsert: (blockKey: string, lang: string, content: Record<string, unknown>) =>
    proxyCall('upsert', 'cmsBlocks', {
      id: `become-dealer-${blockKey}-${lang}`,
      pageKey: 'become-dealer',
      blockKey,
      lang,
      contentJson: JSON.stringify(content),
      updatedAt: new Date().toISOString(),
    }),
};

export async function listCmsBlocksByPage(pageKey: string): Promise<DbResult<CmsBlock[]>> {
  const r = await cmsBlocksDb.getByPage(pageKey);
  if (!r.ok) { console.error('[adminDb] listCmsBlocksByPage failed:', r.error); return r; }
  const data = Array.isArray(r.data) ? r.data : [];
  console.log('[adminDb] listCmsBlocksByPage ok, pageKey:', pageKey, 'count:', data.length);
  return { ok: true, data };
}
